// BackdropImage component handles rendering and managing a backdrop image in a 3D scene
// It supports dragging, scaling, and proper texture management with cleanup
import React, { useEffect, useState, useRef } from 'react';
import * as THREE from 'three';
import { observer } from 'mobx-react-lite';
import { floorplannerStore } from '../../store/floorplannerStore';
import { editorStore } from '../../store/editorStore';
import { BackdropImageType, isBackdropImageType } from '../../types/floorplannerTypes';
import { ThreeEvent, invalidate, useThree } from '@react-three/fiber';
import { projectToWorld } from './projectToWorld';
import { consumeShouldSuppressClickAfterPanningDrag } from '../../utils/panningGesture';
import {
  isBackdropSelectableOnCanvas,
  isBackdropVisible,
  resolveBackdropOpacity,
} from '../../utils/backdropImage';
import { CANVAS_ACCENT_COLOR } from "../../constants/brandColors";

// Props interface defines required properties for the backdrop image
// symbol: Contains backdrop-specific properties like size and ratio
// imagePath: URL/path to the image to be displayed
// position: 2D coordinates where the backdrop should be rendered
interface BackdropImageProps {
  symbol: BackdropImageType;
  imagePath: string;
  position: THREE.Vector2;
}

type BackdropHitTarget = {
  id: string;
  position: THREE.Vector2;
  width: number;
  height: number;
  layerDepth: number;
  layerIndex: number;
  image: HTMLImageElement | null;
  alphaMaskImage?: HTMLImageElement | null;
  alphaCanvas?: HTMLCanvasElement;
  alphaContext?: CanvasRenderingContext2D | null;
  alphaReadable?: boolean;
  alphaMaskCanvas?: HTMLCanvasElement;
  alphaMaskContext?: CanvasRenderingContext2D | null;
  alphaMaskReadable?: boolean;
};

const backdropHitTargets = new Map<string, BackdropHitTarget>();
let activeBackdropPointerId: string | null = null;
const defaultMeshRaycast = THREE.Mesh.prototype.raycast;
const noopRaycast = () => undefined;

// Distance (world units) the backdrop's raycast hits are pushed back so that any
// structural geometry sharing the backdrop's Z plane (walls and lines both live at
// Z≈0) is reported as nearer and therefore receives pointer events first. R3F sorts
// intersections near→far and stops propagation at the first handler, so biasing the
// backdrop slightly behind coincident geometry lets the wall/line claim the click
// while keeping the backdrop in front of the panning/selection planes (at Z=-0.01
// and Z=-10) so an uncovered backdrop is still selectable and draggable.
const BACKDROP_RAYCAST_DEPTH_BIAS = 0.001;

// Raycast wrapper for backdrop meshes: run the normal mesh raycast, then nudge the
// resulting hit distances back by a small bias so coincident structural geometry wins
// the hit-test ordering. Using the real mesh raycast keeps this in lock-step with
// R3F's own event hit-testing (no separate raycaster that could disagree about hits).
const biasedBackdropRaycast = function (
  this: THREE.Mesh,
  raycaster: THREE.Raycaster,
  intersects: THREE.Intersection[],
) {
  const firstNewHit = intersects.length;
  defaultMeshRaycast.call(this, raycaster, intersects);
  for (let i = firstNewHit; i < intersects.length; i++) {
    intersects[i].distance += BACKDROP_RAYCAST_DEPTH_BIAS;
  }
};

// Walk an object and its ancestors looking for a backdrop marker. With no `id` it
// reports whether the object belongs to ANY backdrop; with an `id` it matches one.
const objectBelongsToBackdrop = (object: THREE.Object3D, id?: string): boolean => {
  let current: THREE.Object3D | null = object;
  while (current) {
    const backdropId = current.userData?.backdropId;
    if (backdropId !== undefined) {
      return id === undefined ? true : backdropId === id;
    }
    current = current.parent;
  }
  return false;
};

// The panning/selection/construction/fill planes are full-canvas interaction helpers
// (some, like the area-construction plane, are always mounted and even sit in front of
// the backdrop) and must never count as a real object "on top of" the backdrop. They all
// use a "*-plane" userData id, whereas real objects (walls/lines/symbols) use 9-char
// base36 ids that contain no hyphen, so matching the suffix is safe and future-proof.
const objectIsHelperPlane = (object: THREE.Object3D): boolean => {
  let current: THREE.Object3D | null = object;
  while (current) {
    const id = current.userData?.id;
    if (typeof id === 'string' && id.endsWith('-plane')) {
      return true;
    }
    current = current.parent;
  }
  return false;
};

// Inspect the hits R3F computed for THIS pointer event and decide whether a structural
// object (wall/line/symbol/handle) sits at or in front of the backdrop. Reading R3F's
// own intersection list guarantees we agree with R3F's event dispatch about what is
// under the pointer - the backdrop then yields so the element on top stays selectable.
const pointerEventHasStructureInFront = (
  intersections: THREE.Intersection[] | undefined,
  backdropId: string,
): boolean => {
  if (!intersections || intersections.length === 0) {
    return false;
  }

  // Distance to this backdrop's own hit (intersections are sorted near→far).
  let backdropDistance = Infinity;
  for (const hit of intersections) {
    if (objectBelongsToBackdrop(hit.object, backdropId)) {
      backdropDistance = hit.distance;
      break;
    }
  }

  // Tolerance covers the raycast depth bias plus floating-point noise so geometry
  // sharing the backdrop's Z plane (e.g. walls) still counts as being in front.
  const tolerance = BACKDROP_RAYCAST_DEPTH_BIAS + 0.0005;
  for (const hit of intersections) {
    if (hit.distance > backdropDistance + tolerance) {
      break;
    }
    if (objectBelongsToBackdrop(hit.object) || objectIsHelperPlane(hit.object)) {
      continue;
    }
    return true;
  }

  return false;
};

export const isBackdropCanvasInteractionModeActive = () => (
  editorStore.view3D ||
  editorStore.panning ||
  editorStore.wallConstructionMode ||
  editorStore.lineConstructionMode ||
  editorStore.rulerConstructionMode ||
  editorStore.areaConstructionMode ||
  editorStore.areaFillConstructionMode ||
  editorStore.areaBleedFillMode
);

export const isBackdropCanvasInteractive = (symbol: unknown) => (
  isBackdropImageType(symbol) &&
  !isBackdropCanvasInteractionModeActive()
);

const selectBackdropById = (id: string) => {
  const symbol = floorplannerStore.symbolsMap.get(id);

  if (!symbol || !isBackdropImageType(symbol)) {
    return;
  }

  editorStore.clearSelections();
  floorplannerStore.updateSymbolProperty(id, "selected", true);
  editorStore.addSelection(symbol);
  invalidate();
};

const imageHasVisiblePixelAt = (target: BackdropHitTarget, u: number, v: number) => {
  if (target.alphaMaskImage) {
    const maskImage = target.alphaMaskImage;

    if (!maskImage.naturalWidth || !maskImage.naturalHeight) {
      return true;
    }

    if (target.alphaMaskReadable === false) {
      return true;
    }

    try {
      if (!target.alphaMaskCanvas) {
        target.alphaMaskCanvas = document.createElement("canvas");
        target.alphaMaskCanvas.width = maskImage.naturalWidth;
        target.alphaMaskCanvas.height = maskImage.naturalHeight;
        target.alphaMaskContext = target.alphaMaskCanvas.getContext("2d", { willReadFrequently: true });
        target.alphaMaskContext?.drawImage(maskImage, 0, 0);
      }

      if (!target.alphaMaskContext) {
        return true;
      }

      const x = Math.min(maskImage.naturalWidth - 1, Math.max(0, Math.floor(u * maskImage.naturalWidth)));
      const y = Math.min(maskImage.naturalHeight - 1, Math.max(0, Math.floor((1 - v) * maskImage.naturalHeight)));
      const pixel = target.alphaMaskContext.getImageData(x, y, 1, 1).data;
      target.alphaMaskReadable = true;

      return (pixel[0] + pixel[1] + pixel[2]) / 3 > 8;
    } catch {
      target.alphaMaskReadable = false;
      return true;
    }
  }

  const image = target.image;

  if (!image || !image.naturalWidth || !image.naturalHeight) {
    return true;
  }

  if (target.alphaReadable === false) {
    return true;
  }

  try {
    if (!target.alphaCanvas) {
      target.alphaCanvas = document.createElement("canvas");
      target.alphaCanvas.width = image.naturalWidth;
      target.alphaCanvas.height = image.naturalHeight;
      target.alphaContext = target.alphaCanvas.getContext("2d", { willReadFrequently: true });
      target.alphaContext?.drawImage(image, 0, 0);
    }

    if (!target.alphaContext) {
      return true;
    }

    const x = Math.min(image.naturalWidth - 1, Math.max(0, Math.floor(u * image.naturalWidth)));
    const y = Math.min(image.naturalHeight - 1, Math.max(0, Math.floor((1 - v) * image.naturalHeight)));
    const alpha = target.alphaContext.getImageData(x, y, 1, 1).data[3];
    target.alphaReadable = true;

    return alpha > 8;
  } catch {
    target.alphaReadable = false;
    return true;
  }
};

export const getBackdropHitTarget = (
  clientX: number,
  clientY: number,
  gl: THREE.WebGLRenderer,
  camera: THREE.Camera,
  options: { allowPanning?: boolean } = {},
) => {
  const hits = Array.from(backdropHitTargets.values()).filter((target) => {
    const symbol = floorplannerStore.symbolsMap.get(target.id);

    if (!isBackdropImageType(symbol)) {
      return false;
    }

    // Locked / hidden underlays must not block floor fills or other selection.
    if (!isBackdropSelectableOnCanvas(symbol)) {
      return false;
    }

    if (options.allowPanning) {
      if (
        editorStore.view3D ||
        editorStore.wallConstructionMode ||
        editorStore.lineConstructionMode ||
        editorStore.rulerConstructionMode ||
        editorStore.areaConstructionMode ||
        editorStore.areaFillConstructionMode ||
        editorStore.areaBleedFillMode
      ) {
        return false;
      }
    } else if (!isBackdropCanvasInteractive(symbol)) {
      return false;
    }

    if (!Number.isFinite(target.width) || !Number.isFinite(target.height) || target.width <= 0 || target.height <= 0) {
      return false;
    }

    const [worldX, worldY] = projectToWorld(clientX, clientY, gl, camera, target.layerDepth);
    const localX = worldX - target.position.x;
    const localY = worldY - target.position.y;
    const u = localX / target.width + 0.5;
    const v = localY / target.height + 0.5;

    if (u < 0 || u > 1 || v < 0 || v > 1) {
      return false;
    }

    return imageHasVisiblePixelAt(target, u, v);
  });

  hits.sort((a, b) => (b.layerDepth - a.layerDepth) || (b.layerIndex - a.layerIndex));

  return hits[0]?.id ?? null;
};

export const BackdropImage: React.FC<BackdropImageProps> = observer(({
  imagePath,
  position,
  symbol,
}) => {
  const { camera, gl } = useThree();
  // State management for the backdrop
  const [texture, setTexture] = useState<THREE.Texture | null>(null);  // Holds the loaded texture
  const [alphaMapTexture, setAlphaMapTexture] = useState<THREE.Texture | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const alphaMaskImageRef = useRef<HTMLImageElement | null>(null);
  const meshRef = useRef<THREE.Mesh>(null);  // Reference to the mesh for direct manipulation
  const [dimensions, setDimensions] = useState({ width: 1, height: 1 });  // Physical dimensions in scene units
  const [localPosition, setLocalPosition] = useState<[number, number]>([position.x, position.y]);
  const localPositionRef = useRef<[number, number]>([position.x, position.y]);
  const dragOffset = useRef<[number, number] | null>(null);
  const pointerStart = useRef<[number, number] | null>(null);
  const rafId = useRef<number | null>(null);
  const pendingPointerPosition = useRef<{ clientX: number; clientY: number } | null>(null);
  const hasDragged = useRef(false);
  const undoStarted = useRef(false);
  const backdropIndex = Array.from(floorplannerStore.symbolsMap.values())
    .filter(isBackdropImageType)
    .findIndex((backdrop) => backdrop.id === symbol.id);
  const layerIndex = Math.max(backdropIndex, 0);
  const layerDepth = 0;
  const outlineDepth = layerDepth + 0.001;
  const renderOrder = -1000 + layerIndex;
  const suppressNextCanvasClick = useRef<((event: MouseEvent) => void) | null>(null);
  const canvasSelectable = isBackdropSelectableOnCanvas(symbol);
  const canHandleCanvasPointer = isBackdropCanvasInteractive(symbol) && canvasSelectable;
  const canReceiveCanvasClick =
    canvasSelectable && (canHandleCanvasPointer || editorStore.panning);
  const meshOpacity = resolveBackdropOpacity(symbol);

  useEffect(() => {
    if (dragOffset.current) {
      return;
    }

    const nextPosition: [number, number] = [position.x, position.y];
    localPositionRef.current = nextPosition;
    setLocalPosition(nextPosition);
  }, [position.x, position.y]);

  const removeCanvasClickSuppression = () => {
    if (suppressNextCanvasClick.current) {
      gl.domElement.removeEventListener("click", suppressNextCanvasClick.current, true);
      suppressNextCanvasClick.current = null;
    }
  };

  const suppressCanvasClickOnce = () => {
    removeCanvasClickSuppression();

    suppressNextCanvasClick.current = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (activeBackdropPointerId) {
        selectBackdropById(activeBackdropPointerId);
        activeBackdropPointerId = null;
      }

      removeCanvasClickSuppression();
    };

    gl.domElement.addEventListener("click", suppressNextCanvasClick.current, true);
  };

  const processPointerMove = (clientX: number, clientY: number) => {
    if (editorStore.panning || symbol.locked || !dragOffset.current || !pointerStart.current) return;

    const deltaX = Math.abs(clientX - pointerStart.current[0]);
    const deltaY = Math.abs(clientY - pointerStart.current[1]);

    if (!hasDragged.current && (deltaX > 1 || deltaY > 1)) {
      hasDragged.current = true;
    }

    if (!undoStarted.current && hasDragged.current) {
      floorplannerStore.beginUndoTransaction("dragBackdropImage");
      floorplannerStore.recordSymbolForUndo(symbol.id);
      undoStarted.current = true;
    }

    const [worldX, worldY] = projectToWorld(clientX, clientY, gl, camera);
    const [offsetX, offsetY] = dragOffset.current;
    const nextPosition: [number, number] = [worldX - offsetX, worldY - offsetY];
    localPositionRef.current = nextPosition;
    setLocalPosition(nextPosition);
  };

  const flushPendingPointerMove = () => {
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }

    const pending = pendingPointerPosition.current;
    pendingPointerPosition.current = null;

    if (pending) {
      processPointerMove(pending.clientX, pending.clientY);
    }
  };

  const finishDrag = () => {
    flushPendingPointerMove();

    gl.domElement.removeEventListener("pointermove", handlePointerMove);
    gl.domElement.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerUp);

    floorplannerStore.setBlockDirty(false);

    if (undoStarted.current || hasDragged.current) {
      floorplannerStore.updateSymbolPosition(
        symbol.id,
        new THREE.Vector2(localPositionRef.current[0], localPositionRef.current[1]),
      );
      floorplannerStore.recordSymbolChange(symbol.id, "modify");
      floorplannerStore.pushToUndoStack();
      floorplannerStore.setDirty();
    }

    dragOffset.current = null;
    pointerStart.current = null;
    pendingPointerPosition.current = null;

    hasDragged.current = false;
    undoStarted.current = false;

    if (activeBackdropPointerId) {
      selectBackdropById(activeBackdropPointerId);
      window.setTimeout(() => {
        if (activeBackdropPointerId === symbol.id) {
          activeBackdropPointerId = null;
          removeCanvasClickSuppression();
        }
      }, 0);
    }
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (editorStore.panning || symbol.locked || !dragOffset.current || !pointerStart.current) return;

    pendingPointerPosition.current = { clientX: event.clientX, clientY: event.clientY };

    if (rafId.current !== null) {
      return;
    }

    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      const pending = pendingPointerPosition.current;
      pendingPointerPosition.current = null;

      if (pending) {
        processPointerMove(pending.clientX, pending.clientY);
      }
    });
  };

  const handlePointerUp = () => {
    finishDrag();
  };

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (event.button !== 0) {
      return;
    }

    if (editorStore.panning || !canHandleCanvasPointer) {
      return;
    }

    if (getBackdropHitTarget(event.clientX, event.clientY, gl, camera) !== symbol.id) {
      return;
    }

    // If a wall/line/symbol is on top of the backdrop here, let it handle the click
    // instead of stealing the selection. Bailing without stopPropagation lets the
    // event continue bubbling to the element in front.
    if (pointerEventHasStructureInFront(event.intersections, symbol.id)) {
      return;
    }

    event.stopPropagation();
    activeBackdropPointerId = symbol.id;
    suppressCanvasClickOnce();
    selectBackdropById(symbol.id);

    if (symbol.locked) {
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
      return;
    }

    const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    dragOffset.current = [worldX - position.x, worldY - position.y];
    localPositionRef.current = [position.x, position.y];
    setLocalPosition(localPositionRef.current);
    pointerStart.current = [event.clientX, event.clientY];
    hasDragged.current = false;
    undoStarted.current = false;

    gl.domElement.addEventListener("pointermove", handlePointerMove);
    gl.domElement.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    floorplannerStore.setBlockDirty(true);
  };

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    if (!editorStore.panning) {
      return;
    }

    if (getBackdropHitTarget(event.clientX, event.clientY, gl, camera, { allowPanning: true }) !== symbol.id) {
      return;
    }

    // Don't claim the click when a wall/line/symbol is rendered on top of the backdrop.
    if (pointerEventHasStructureInFront(event.intersections, symbol.id)) {
      return;
    }

    event.stopPropagation();

    if (consumeShouldSuppressClickAfterPanningDrag()) {
      return;
    }

    selectBackdropById(symbol.id);
  };

  // Main effect for loading and managing the backdrop image
  useEffect(() => {
    if (!imagePath) {
      console.error('No backdrop image path provided');
      return;
    }

    const image = new Image();
    let isActive = true;  // Used to prevent updates after unmount

    // Handler for when the image loads successfully
    image.onload = () => {
      if (!isActive) return;

      // Get the natural dimensions of the loaded image
      const originalWidth = image.width;
      const originalHeight = image.height;
      const aspectRatio = originalWidth / originalHeight;
      
      // Calculate real-world dimensions based on backdrop properties
      // backdropSize: measurement in millimeters (e.g., 1000mm = 1m)
      // backdropRatio: what portion of the image width that measurement represents
      // Example: if size=1000mm and ratio=0.5, the full image width is 2000mm (2m)
      const safeSize = Math.max(symbol.backdropSize ?? 10, 0.1);  // in millimeters
      const safeRatio = Math.max(symbol.backdropRatio ?? 1, 0.0001);

      // Calculate final dimensions in scene units (meters)
      const fullWidthMm = safeSize / safeRatio;
      const planeWidth = fullWidthMm / 1000;  // Convert mm to meters
      const planeHeight = planeWidth / aspectRatio;  // Maintain aspect ratio

      // Update the dimensions state for the plane geometry
      setDimensions({
        width: planeWidth,
        height: planeHeight,
      });

      // Create texture directly from the loaded image
      const texture = new THREE.Texture(image);
      texture.needsUpdate = true;
      texture.generateMipmaps = false;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;

      setTexture(texture);
      imageRef.current = image;
    };

    // Handle image loading errors
    image.onerror = (error) => {
      console.error('Error loading image:', error);
    };

    // Set crossOrigin to allow loading from different domains
    image.crossOrigin = 'anonymous';
    
    // Start loading the image
    image.src = imagePath;

    // Cleanup function to prevent memory leaks
    return () => {
      isActive = false;
      if (texture) {
        texture.dispose();
      }
      imageRef.current = null;
      backdropHitTargets.delete(symbol.id);
      removeCanvasClickSuppression();
      finishDrag();
    };
  }, [imagePath, symbol.backdropSize, symbol.backdropRatio]);

  useEffect(() => {
    if (!symbol.backdropAlphaMask) {
      setAlphaMapTexture(null);
      alphaMaskImageRef.current = null;
      return;
    }

    const maskImage = new Image();
    let isActive = true;
    let loadedTexture: THREE.Texture | null = null;

    maskImage.onload = () => {
      if (!isActive) return;

      const texture = new THREE.Texture(maskImage);
      texture.needsUpdate = true;
      texture.generateMipmaps = false;
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;
      loadedTexture = texture;

      setAlphaMapTexture(texture);
      alphaMaskImageRef.current = maskImage;
    };

    maskImage.onerror = (error) => {
      console.error('Error loading backdrop alpha mask:', error);
      setAlphaMapTexture(null);
      alphaMaskImageRef.current = null;
    };

    if (!symbol.backdropAlphaMask.startsWith("data:")) {
      maskImage.crossOrigin = 'anonymous';
    }
    maskImage.src = symbol.backdropAlphaMask;

    return () => {
      isActive = false;
      alphaMaskImageRef.current = null;
      loadedTexture?.dispose();
    };
  }, [symbol.backdropAlphaMask]);

  useEffect(() => {
    if (!texture || !isBackdropSelectableOnCanvas(symbol)) {
      backdropHitTargets.delete(symbol.id);
      return;
    }

    backdropHitTargets.set(symbol.id, {
      id: symbol.id,
      position: new THREE.Vector2(position.x, position.y),
      width: dimensions.width,
      height: dimensions.height,
      layerDepth,
      layerIndex,
      image: imageRef.current,
      alphaMaskImage: alphaMaskImageRef.current,
    });

    return () => {
      backdropHitTargets.delete(symbol.id);
    };
  }, [
    texture,
    alphaMapTexture,
    symbol.id,
    symbol.locked,
    symbol.visible,
    position.x,
    position.y,
    dimensions.width,
    dimensions.height,
    layerDepth,
    layerIndex,
  ]);

  // Don't render anything until texture is loaded, or when the user hid the image.
  if (!texture || !isBackdropVisible(symbol)) {
    return null;
  }

  const backdropMesh = (
    <>
      <mesh
        ref={meshRef}
        position={[0, 0, layerDepth]}
        renderOrder={renderOrder}
        userData={{ backdropId: symbol.id }}
        raycast={canReceiveCanvasClick ? biasedBackdropRaycast : noopRaycast}
        onPointerDown={handlePointerDown}
        onPointerUp={(event) => {
          if (canHandleCanvasPointer && activeBackdropPointerId) {
            event.stopPropagation();
          }
        }}
        onClick={handleClick}
      >
        <planeGeometry args={[dimensions.width, dimensions.height]} />
        <meshBasicMaterial
          map={texture}
          alphaMap={alphaMapTexture ?? undefined}
          alphaTest={alphaMapTexture ? 0.5 : 0}
          transparent={true}
          side={THREE.DoubleSide}
          depthTest={true}
          depthWrite={false}
          opacity={meshOpacity}
          toneMapped={false}
        />
      </mesh>

      {symbol.selected && (
        <mesh position={[0, 0, outlineDepth]} renderOrder={renderOrder + 1} raycast={() => null}>
          <planeGeometry args={[dimensions.width + 0.05, dimensions.height + 0.05]} />
          <meshBasicMaterial
            color={CANVAS_ACCENT_COLOR}
            transparent={true}
            opacity={0.3}
            side={THREE.DoubleSide}
            depthTest={true}
            depthWrite={false}
          />
        </mesh>
      )}
    </>
  );

  // Render the backdrop as a draggable, selectable textured plane
  return (
    <group position={[localPosition[0], localPosition[1], 0]}>
      {backdropMesh}
    </group>
  );
});

// Export a function to handle backdrop deselection for use in other components
export const deselectBackdrop = (id: string) => {
  // Get the complete symbol object from the store
  const symbol = floorplannerStore.symbolsMap.get(id);
  if (symbol) {
    editorStore.removeSelection(symbol);
    floorplannerStore.updateSymbolProperty(id, "selected", false);
    invalidate();
  }
};
